import GoToArticle from "@components/Actions/GoToArticle";
import { getClientDomain } from "@core/utils/getClientDomain";
import { useCtrlKey } from "@core-ui/hooks/useCtrlKey";
import useMediaQuery from "@core-ui/hooks/useMediaQuery";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import { tryCopyToClipboard } from "@core-ui/utils/clipboard";
import { cssMedia } from "@core-ui/utils/cssUtils";
// biome-ignore lint/style/noRestrictedImports: idc
import styled from "@emotion/styled";
import t from "@ext/localization/locale/translate";
import type { LinkMenuMode } from "@ext/markdown/elements/link/edit/components/LinkMenu/LinkMenu";
import { getLinkToHeading } from "@ext/markdown/elements/link/edit/logic/getLinkToHeading";
import { getCachedSetting } from "@ext/settings/logic/cachedSettingsStore";
import { GlassToolbar, GlassToolbarIcon, GlassToolbarText, GlassToolbarToggleButton } from "@ui-kit/GlassToolbar";
import type { IconCode } from "@ui-kit/Icon";
import { type ComponentProps, type HTMLAttributes, memo, useState } from "react";

interface ButtonViewProps {
	href: string;
	icon: IconCode;
	itemName: string;
	isExternalLink: boolean;
}

interface ViewLinkMenuProps extends ButtonViewProps {
	onDelete: () => void;
	setMode: (mode: LinkMenuMode) => void;
}

const Container = styled.div`
	width: 100%;
	max-width: 100%;

	> div {
		max-width: 100%;
	}
`;

const TooltipToolbarButton = (props: ComponentProps<typeof GlassToolbarToggleButton> & { tooltipText: string }) => {
	return <GlassToolbarToggleButton tooltipText={props.tooltipText} {...props}></GlassToolbarToggleButton>;
};

const CopyButton = (props: HTMLAttributes<HTMLButtonElement> & { href: string }) => {
	const { href } = props;
	const [isCopied, setIsCopied] = useState(false);
	const webEditorUrl = getCachedSetting("services.web-editor.endpoint");

	const onClickHandler = () => {
		let linkToCopy: string;

		if (URL.canParse(href)) linkToCopy = new URL(href).href;
		else linkToCopy = getClientDomain(webEditorUrl) + href;

		tryCopyToClipboard(linkToCopy, { showPopover: false }).then((copied) => copied && setIsCopied(true));
	};

	return (
		<TooltipToolbarButton
			onClick={onClickHandler}
			onMouseLeave={() => setIsCopied(false)}
			tooltipText={isCopied ? `${t("copied")}!` : t("copy")}
		>
			<GlassToolbarIcon icon={isCopied ? "check" : "copy"} />
		</TooltipToolbarButton>
	);
};

const ButtonView = ({ href, icon, itemName, isExternalLink }: ButtonViewProps) => {
	const { isCtrlPressed } = useCtrlKey();
	const { isTauri } = usePlatform();

	const desktopBehavior = isExternalLink ? "_blank" : "_self";
	const browserBehavior = isExternalLink || isCtrlPressed ? "_blank" : "_self";
	const target = isTauri ? desktopBehavior : browserBehavior;

	const hashHatch = getLinkToHeading(href);
	const isCurrentLink = typeof window !== "undefined" ? window.location.pathname === hashHatch?.path : false;
	const hrefStartByHash = href.startsWith("#");
	const isHashLink = hashHatch?.hash && (isCurrentLink || hrefStartByHash);

	const toolbarButton = (
		<Container>
			<GlassToolbarToggleButton
				className="text-left flex-1 min-w-0 overflow-hidden justify-start w-full"
				focusable
			>
				<GlassToolbarIcon className="flex-shrink-0" icon={icon} />
				<GlassToolbarText className="truncate min-w-0 text-xs">{itemName}</GlassToolbarText>
			</GlassToolbarToggleButton>
		</Container>
	);

	return target === "_blank" || isHashLink ? (
		<a
			className="flex flex-1 min-w-0 overflow-hidden"
			href={isHashLink ? hashHatch?.hash : href}
			rel="noopener noreferrer"
			target={target}
		>
			{toolbarButton}
		</a>
	) : (
		<GoToArticle
			className="flex flex-1 min-w-0 overflow-hidden"
			containerClassName="flex-1 min-w-0"
			href={href}
			style={{ textDecorationLine: "none" }}
			trigger={toolbarButton}
		/>
	);
};

export const ViewLinkMenu = memo(({ href, icon, onDelete, setMode, itemName, isExternalLink }: ViewLinkMenuProps) => {
	const isMobile = useMediaQuery(cssMedia.JSnarrow);

	return (
		// biome-ignore lint/a11y/useValidAriaRole: expected
		<GlassToolbar
			className="flex overflow-hidden shadow-glass-xl"
			role="link-toolbar"
			style={{ width: isMobile ? "100%" : "18.75rem" }}
		>
			<ButtonView href={href} icon={icon} isExternalLink={isExternalLink} itemName={itemName} />
			<TooltipToolbarButton onClick={() => setMode("edit")} tooltipText={t("edit2")}>
				<GlassToolbarIcon icon="pencil" />
			</TooltipToolbarButton>
			<CopyButton href={href} />
			<TooltipToolbarButton onClick={() => onDelete()} tooltipText={t("remove-link")}>
				<GlassToolbarIcon icon="unlink" />
			</TooltipToolbarButton>
		</GlassToolbar>
	);
});
